import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Activity,
  BarChart3,
  Calendar,
  CheckCircle2,
  CircleDashed,
  Clock,
  ListTodo,
  PieChart as PieIcon,
  Sparkles,
  TrendingDown,
  Users,
} from 'lucide-react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { EmptyState } from '@/components/common/empty-state';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api';
import {
  getBurndownApi,
  getProjectDashboardApi,
  type BurndownResponse,
  type ProjectDashboardResponse,
} from '@/lib/discovery-api';
import { getProjectByKey } from '@/lib/project-api';
import { cn } from '@/lib/utils';

type RangeKey = '7d' | '30d' | 'sprint' | 'custom';

const RANGE_LABEL: Record<RangeKey, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  sprint: 'Active sprint',
  custom: 'Custom range',
};

const TYPE_COLORS: Record<string, string> = {
  STORY: '#10b981',
  TASK: '#0ea5e9',
  BUG: '#f43f5e',
  SUBTASK: '#64748b',
};

export function ProjectDashboardPage() {
  const { orgId, projectKey } = useOutletContext<ProjectLayoutContext>();
  const [params, setParams] = useSearchParams();
  const range = (params.get('range') as RangeKey | null) ?? 'sprint';
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;

  const setRange = (next: RangeKey) => {
    const p = new URLSearchParams(params);
    p.set('range', next);
    if (next !== 'custom') {
      p.delete('from');
      p.delete('to');
    }
    setParams(p, { replace: true });
  };

  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });

  const dashboardQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'dashboard', { range, from, to }],
    queryFn: () =>
      projectQuery.data
        ? getProjectDashboardApi(orgId, projectQuery.data.id, {
            range,
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          })
        : Promise.resolve<ProjectDashboardResponse | null>(null),
    enabled: Boolean(projectQuery.data),
  });

  const burndownQuery = useQuery({
    queryKey: [
      'org',
      orgId,
      'project',
      projectKey,
      'burndown',
      dashboardQuery.data?.activeSprintId,
    ],
    queryFn: () =>
      projectQuery.data && dashboardQuery.data?.activeSprintId
        ? getBurndownApi(orgId, projectQuery.data.id, dashboardQuery.data.activeSprintId)
        : Promise.resolve<BurndownResponse | null>(null),
    enabled: Boolean(projectQuery.data && dashboardQuery.data?.activeSprintId),
  });

  const data = dashboardQuery.data;
  const doneRatio =
    data && data.totalIssues > 0
      ? Math.round(((data.totalIssues - data.totalOpenIssues) / data.totalIssues) * 100)
      : 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Activity className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>Overview of</span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-xs font-medium ring-1 ring-border">
                  {projectKey}
                </span>
                <span>·</span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 text-xs font-medium ring-1 ring-border">
                  <Calendar className="h-3 w-3" aria-hidden />
                  {RANGE_LABEL[range]}
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Clock className="h-3 w-3" aria-hidden /> Range
              </Label>
              <Select
                value={range}
                onChange={(e) => setRange(e.target.value as RangeKey)}
                wrapperClassName="w-48"
                className="text-xs"
              >
                <option value="sprint">Active sprint</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="custom">Custom…</option>
              </Select>
            </div>
            {range === 'custom' ? (
              <>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Calendar className="h-3 w-3" aria-hidden /> From
                  </Label>
                  <Input
                    type="date"
                    className="h-10 text-xs"
                    value={from ? from.slice(0, 10) : ''}
                    onChange={(e) => {
                      const p = new URLSearchParams(params);
                      if (e.target.value) p.set('from', new Date(e.target.value).toISOString());
                      else p.delete('from');
                      setParams(p, { replace: true });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Calendar className="h-3 w-3" aria-hidden /> To
                  </Label>
                  <Input
                    type="date"
                    className="h-10 text-xs"
                    value={to ? to.slice(0, 10) : ''}
                    onChange={(e) => {
                      const p = new URLSearchParams(params);
                      if (e.target.value) p.set('to', new Date(e.target.value).toISOString());
                      else p.delete('to');
                      setParams(p, { replace: true });
                    }}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px border-t bg-border sm:grid-cols-4">
          <KpiTile
            icon={ListTodo}
            label="Total issues"
            value={data ? String(data.totalIssues) : '—'}
          />
          <KpiTile
            icon={CircleDashed}
            label="Open issues"
            value={data ? String(data.totalOpenIssues) : '—'}
            tone="amber"
          />
          <KpiTile
            icon={CheckCircle2}
            label="Completion"
            value={data ? `${doneRatio}%` : '—'}
            tone={doneRatio >= 100 ? 'emerald' : 'primary'}
          />
          <KpiTile
            icon={Sparkles}
            label="Active sprint"
            value={data?.activeSprintId ? 'Yes' : 'No'}
            tone={data?.activeSprintId ? 'emerald' : 'muted'}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardWidget
          title="Burndown"
          description="Story points remaining across the sprint."
          icon={TrendingDown}
          accent="sky"
          isLoading={burndownQuery.isLoading}
          isError={burndownQuery.isError}
          errorMessage={extractApiErrorMessage(burndownQuery.error)}
          onRetry={() => burndownQuery.refetch()}
          isEmpty={!burndownQuery.data || burndownQuery.data.days.length === 0}
          emptyLabel={
            dashboardQuery.data?.activeSprintId
              ? 'No burndown data yet — no status transitions in the sprint window.'
              : 'No active sprint — start one to track burndown.'
          }
        >
          {burndownQuery.data ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={burndownQuery.data.days} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="actualLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0ea5e9" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#94a3b833' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  allowDecimals={false}
                  axisLine={{ stroke: '#94a3b833' }}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Line
                  type="monotone"
                  dataKey="idealRemaining"
                  name="Ideal"
                  stroke="#94a3b8"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="actualRemaining"
                  name="Actual"
                  stroke="url(#actualLine)"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 0, fill: '#0ea5e9' }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : null}
        </DashboardWidget>

        <DashboardWidget
          title="Issue type distribution"
          description="Share of stories, tasks, bugs, and subtasks."
          icon={PieIcon}
          accent="violet"
          isLoading={dashboardQuery.isLoading}
          isError={dashboardQuery.isError}
          errorMessage={extractApiErrorMessage(dashboardQuery.error)}
          onRetry={() => dashboardQuery.refetch()}
          isEmpty={!dashboardQuery.data || dashboardQuery.data.issueTypeDistribution.length === 0}
          emptyLabel="No issues in the selected range."
        >
          {dashboardQuery.data ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={dashboardQuery.data.issueTypeDistribution}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  innerRadius={54}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="#ffffff"
                  strokeWidth={2}
                  label={(p) => `${p.type} ${p.count}`}
                  labelLine={false}
                >
                  {dashboardQuery.data.issueTypeDistribution.map((slice) => (
                    <Cell key={slice.type} fill={TYPE_COLORS[slice.type] ?? '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          ) : null}
        </DashboardWidget>

        <DashboardWidget
          title="Status distribution"
          description="Where issues currently sit on the board."
          icon={BarChart3}
          accent="emerald"
          isLoading={dashboardQuery.isLoading}
          isError={dashboardQuery.isError}
          errorMessage={extractApiErrorMessage(dashboardQuery.error)}
          onRetry={() => dashboardQuery.refetch()}
          isEmpty={!dashboardQuery.data || dashboardQuery.data.statusDistribution.length === 0}
          emptyLabel="No statuses yet."
        >
          {dashboardQuery.data ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={dashboardQuery.data.statusDistribution}
                margin={{ top: 10, right: 12, left: -10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" vertical={false} />
                <XAxis
                  dataKey="status"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#94a3b833' }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#94a3b833' }}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: '#94a3b820' }} />
                <Bar dataKey="count" name="Issues" radius={[6, 6, 0, 0]}>
                  {dashboardQuery.data.statusDistribution.map((slice) => (
                    <Cell key={slice.status} fill={slice.colorHex} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </DashboardWidget>

        <DashboardWidget
          title="Top assignees"
          description="Who is carrying open work right now."
          icon={Users}
          accent="rose"
          isLoading={dashboardQuery.isLoading}
          isError={dashboardQuery.isError}
          errorMessage={extractApiErrorMessage(dashboardQuery.error)}
          onRetry={() => dashboardQuery.refetch()}
          isEmpty={!dashboardQuery.data || dashboardQuery.data.topAssignees.length === 0}
          emptyLabel="No open issues with assignees."
        >
          {dashboardQuery.data ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={dashboardQuery.data.topAssignees}
                layout="vertical"
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="assigneeBar" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#f43f5e" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#94a3b833' }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  axisLine={false}
                  tickLine={false}
                  width={140}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: '#94a3b820' }} />
                <Bar
                  dataKey="openCount"
                  name="Open issues"
                  fill="url(#assigneeBar)"
                  radius={[0, 6, 6, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </DashboardWidget>
      </div>
    </div>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tone?: 'primary' | 'emerald' | 'amber' | 'muted';
}) {
  const toneCls =
    tone === 'emerald'
      ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400'
      : tone === 'amber'
        ? 'bg-amber-500/10 text-amber-600 ring-amber-500/30 dark:text-amber-400'
        : tone === 'muted'
          ? 'bg-muted text-muted-foreground ring-border'
          : 'bg-primary/5 text-primary ring-primary/20';
  return (
    <div className="flex items-center gap-3 bg-background px-4 py-3">
      <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl ring-1', toneCls)}>
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-xl font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function DashboardWidget({
  title,
  description,
  icon: Icon,
  accent,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  isEmpty,
  emptyLabel,
  children,
}: {
  title: string;
  description?: string;
  icon: typeof BarChart3;
  accent: 'sky' | 'violet' | 'emerald' | 'rose';
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  isEmpty: boolean;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  const accentCls =
    accent === 'sky'
      ? {
          strip: 'from-sky-500 via-sky-400 to-transparent',
          iconBg: 'bg-sky-500/10 text-sky-600 ring-sky-500/20 dark:text-sky-400',
        }
      : accent === 'violet'
        ? {
            strip: 'from-violet-500 via-violet-400 to-transparent',
            iconBg: 'bg-violet-500/10 text-violet-600 ring-violet-500/20 dark:text-violet-400',
          }
        : accent === 'emerald'
          ? {
              strip: 'from-emerald-500 via-emerald-400 to-transparent',
              iconBg: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400',
            }
          : {
              strip: 'from-rose-500 via-rose-400 to-transparent',
              iconBg: 'bg-rose-500/10 text-rose-600 ring-rose-500/20 dark:text-rose-400',
            };
  return (
    <Card className="overflow-hidden">
      <div className={cn('h-1 w-full bg-gradient-to-r', accentCls.strip)} />
      <div className="flex items-start gap-3 border-b bg-muted/30 px-4 py-3">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg ring-1', accentCls.iconBg)}>
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{title}</h3>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
      </div>
      <CardContent className="p-3">
        {isLoading ? (
          <Skeleton className="h-60 w-full" />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 py-10 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background ring-1 ring-border">
              <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
            </div>
            <p className="text-sm font-medium">{errorMessage ?? 'Could not load widget.'}</p>
            {onRetry ? (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : isEmpty ? (
          <EmptyState title="No data" description={emptyLabel} />
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

type TooltipPayload = Array<{
  name?: string;
  value?: number | string;
  color?: string;
  payload?: Record<string, unknown>;
}>;

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload;
  label?: string | number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border bg-background/95 px-3 py-2 text-xs shadow-md backdrop-blur">
      {label != null ? (
        <div className="mb-1 font-semibold text-foreground">{String(label)}</div>
      ) : null}
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: entry.color ?? '#64748b' }}
            aria-hidden
          />
          <span className="text-muted-foreground">{entry.name}</span>
          <span className="ml-auto font-semibold tabular-nums">{String(entry.value ?? '')}</span>
        </div>
      ))}
    </div>
  );
}
